import React, { useEffect, useMemo, useState } from 'react';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';
import PacketBuilder from '../components/PacketBuilder';
import ShowCenterHub from '../components/ShowCenterHub';

const TABS = ['Shows', 'Packet Builder', 'Credentials', 'USEF Vaccine Form', 'Secretaries', 'Send Packet', 'Sent History'];

const CREDENTIAL_TYPES = [
  'USEF Member Card',
  'USEF Horse ID / Horse Recording',
  'USEF Equine Vaccination Record',
  'Coggins',
  'Health Certificate',
  'USDF Member Card',
  'USDF Horse Card / HID / Lifetime Registration',
  'USHJA Member Card',
  'USHJA Horse Recording / Online Horse Card',
  'USEA Member Card',
  'USEA Horse Registration / Horse Card',
  'AHA Membership / Competition Card',
  'AHA Registration Papers',
  'AQHA Membership Card',
  'AQHA Registration Papers',
  'APHA Membership / Show Card',
  'APHA Registration Papers',
  'Amateur Card / Amateur Status',
  'Youth Card / Youth Eligibility',
  'Non-Pro Card',
  'FEI Passport',
  'National Passport',
  'Insurance',
  'Prize List / Entry Form',
  'Other / Custom Credential'
];

const APPLIES_TO = ['horse', 'rider', 'owner', 'trainer', 'coach', 'handler', 'barn', 'show'];

const fmtDate = (value) => {
  if (!value) return '—';
  try { return new Date(value).toLocaleDateString(); } catch { return value; }
};

const makePacketSubject = (show) => {
  if (!show) return '';
  const dates = [show.start_date, show.end_date].filter(Boolean).map(fmtDate).join(' - ');
  return `${show.show_name || 'Show'}${dates ? ` - ${dates}` : ''} - Entry Documents for ${show.exhibitor_name || 'Exhibitor'}`;
};

const makePacketBody = (show, selectedCredentials) => {
  if (!show) return '';
  const horses = Array.isArray(show.horses) ? show.horses.map(h => h.horse_name).filter(Boolean).join(', ') : '';
  const docs = selectedCredentials.length ? selectedCredentials.map(c => `- ${c.display_name || c.credential_type}`).join('\n') : '- Selected entry documents';
  return `Hello,\n\nPlease find attached the entry documents for:\n\nShow: ${show.show_name || ''}\nShow dates: ${[show.start_date, show.end_date].filter(Boolean).map(fmtDate).join(' - ')}\nExhibitor: ${show.exhibitor_name || ''}\nHorse(s): ${horses}\n\nAttached documents:\n${docs}\n\nThank you,\n${show.exhibitor_name || ''}`;
};

const Field = ({ label, children, hint }) => (
  <label style={{ display: 'block', marginBottom: 10 }}>
    <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>{label}</div>
    {children}
    {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{hint}</div>}
  </label>
);

const Card = ({ children, style }) => <div className="card" style={{ padding: 14, marginBottom: 12, ...style }}>{children}</div>;

const ShowsPage = () => {
  const [activeTab, setActiveTab] = useState('Home');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ horses: [], secretaries: [], credentials: [], shows: [], sends: [] });
  const [selectedFiles, setSelectedFiles] = useState({});
  const [showEntryMode, setShowEntryMode] = useState('chooser');
  const [prizeListFile, setPrizeListFile] = useState(null);
  const [prizeListImport, setPrizeListImport] = useState(null);
  const [prizeListImporting, setPrizeListImporting] = useState(false);
  const [prizeParseBusy, setPrizeParseBusy] = useState(false);
  const [prizeReview, setPrizeReview] = useState(null);
  const [prizeClassSearch, setPrizeClassSearch] = useState('');
  const [recentPrizeImports, setRecentPrizeImports] = useState([]);

  const [entryProfile, setEntryProfile] = useState(null);
  const [entryProfileSaving, setEntryProfileSaving] = useState(false);
  const [entryShowId, setEntryShowId] = useState('');
  const [entryHorseIds, setEntryHorseIds] = useState([]);
  const [entryCreating, setEntryCreating] = useState(false);
  const [createdEntry, setCreatedEntry] = useState(null);
  const [entryDetail, setEntryDetail] = useState(null);
  const [entryEditBusy, setEntryEditBusy] = useState(false);

  const [entryClassForm, setEntryClassForm] = useState({
    entry_horse_id: '',
    class_number: '',
    class_name: '',
    fee: ''
  });

  const [entryFeeForm, setEntryFeeForm] = useState({
    entry_horse_id: '',
    label: '',
    amount: '',
    quantity: '1',
    scope: 'entry'
  });

  const [secretaryForm, setSecretaryForm] = useState({ name: '', email: '', phone: '', organization: '', notes: '' });
  const [showForm, setShowForm] = useState({ show_name: '', start_date: '', end_date: '', venue_name: '', city: '', state: '', secretary_id: '', exhibitor_name: '', entry_method: 'online_platform', status: 'preparing', notes: '', prize_list_import_id: '', entry_deadline: '', late_entry_deadline: '' });
  const [showHorseForm, setShowHorseForm] = useState({ show_id: '', horse_id: '', rider_name: '', owner_name: '', trainer_name: '', class_list: '', stabling_notes: '', notes: '' });
  const [credentialForm, setCredentialForm] = useState({ applies_to: 'horse', horse_id: '', credential_type: 'USEF Member Card', organization: '', display_name: '', subject_name: '', id_number: '', status: 'active', issue_date: '', expiration_date: '', notes: '', needs_front_back: true });
  const [vaccineForm, setVaccineForm] = useState({ horse_id: '', horse_name_text: '', owner_name: '', flu_date: '', flu_name: '', flu_serial: '', flu_expiration: '', ehv_date: '', ehv_name: '', ehv_serial: '', ehv_expiration: '', veterinarian_name: '', clinic_name: '', vet_signature_present: false, owner_signature_present: false, date_signed: '', notes: '' });
  const [packetForm, setPacketForm] = useState({ show_id: '', secretary_id: '', to_email: '', subject: '', body: '', credential_file_ids: [] });

  const horses = data.horses || [];
  const secretaries = data.secretaries || [];
  const credentials = data.credentials || [];
  const shows = data.shows || [];
  const sends = data.sends || [];

  const selectedShow = shows.find(s => s.id === packetForm.show_id) || null;
  const selectedCredentialFiles = useMemo(() => {
    const ids = new Set(packetForm.credential_file_ids || []);
    return credentials.flatMap(c => (c.files || []).filter(f => ids.has(f.id)).map(f => ({ ...f, credential: c })));
  }, [credentials, packetForm.credential_file_ids]);

  useEffect(() => { load(); }, []);


  useEffect(() => {
    if (!prizeListImport?.id || !['processing', 'queued'].includes(prizeListImport.status)) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const { data: current } = await api.get(`/show-center/prize-lists/${prizeListImport.id}`);
        if (!cancelled) setPrizeListImport(current);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.message || 'Could not check prize-list import');
      }
    };

    const timer = setInterval(poll, 2000);
    poll();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [prizeListImport?.id, prizeListImport?.status]);

  const load = async () => {
    try {
      const resp = await api.get('/show-center/bootstrap');
      setData(resp.data);
      const firstShow = resp.data.shows?.[0];
      if (firstShow) {
        setShowHorseForm(prev => prev.show_id ? prev : { ...prev, show_id: firstShow.id });
        setPacketForm(prev => prev.show_id ? prev : { ...prev, show_id: firstShow.id, secretary_id: firstShow.secretary_id || '', to_email: firstShow.secretary_email || '', subject: makePacketSubject(firstShow), body: makePacketBody(firstShow, []) });
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load Show Center');
    } finally {
      setLoading(false);
    }
  };

  const update = (setter, key, value) => setter(prev => ({ ...prev, [key]: value }));

  const openPrizeImporter = async () => {
    setShowEntryMode('import');
    setPrizeListFile(null);
    setPrizeListImport(null);
    setPrizeReview(null);
    setPrizeClassSearch('');

    try {
      const { data: recent } = await api.get(
        '/show-center/prize-lists/recent'
      );

      setRecentPrizeImports(
        Array.isArray(recent) ? recent : []
      );
    } catch (e) {
      setRecentPrizeImports([]);
    }
  };

  const resumePrizeListImport = async importId => {
    try {
      const { data: current } = await api.get(
        `/show-center/prize-lists/${importId}`
      );

      setPrizeListImport(current);
      setPrizeReview(current.parsed_data || null);
      setPrizeClassSearch('');
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        'Could not load prize list'
      );
    }
  };

  const analyzePrizeList = async () => {
    if (!prizeListImport?.id) {
      return toast.error('No prize list selected');
    }

    try {
      setPrizeParseBusy(true);

      const { data: analyzed } = await api.post(
        `/show-center/prize-lists/${prizeListImport.id}/analyze`
      );

      setPrizeListImport(analyzed);
      setPrizeReview(analyzed.parsed_data || null);

      setAiReadFailed(false);
      toast.success('Show details read from the prize list');
    } catch (e) {
      setAiReadFailed(true);
      toast.error(
        e.response?.data?.message ||
        'Failed to read the prize list'
      );
    } finally {
      setPrizeParseBusy(false);
    }
  };

  const [aiReadFailed, setAiReadFailed] = useState(false);
  const [aiElapsed, setAiElapsed] = useState(0);
  useEffect(() => {
    if (!prizeParseBusy) { setAiElapsed(0); return undefined; }
    const t0 = Date.now();
    const iv = setInterval(() => setAiElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [prizeParseBusy]);
  useEffect(() => {
    const busy = prizeParseBusy || ['processing', 'queued'].includes(prizeListImport?.status);
    if (!busy) return undefined;
    const warn = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [prizeParseBusy, prizeListImport]);
  const aiBusyMessage = aiElapsed < 6 ? 'Reading show details…'
    : aiElapsed < 16 ? 'API busy — retrying (1 of 3)…'
    : aiElapsed < 30 ? 'API busy — retrying (2 of 3)…'
    : aiElapsed < 55 ? 'API busy — retrying (3 of 3)…'
    : 'Still trying — hang tight…';
  const [autoAnalyzedId, setAutoAnalyzedId] = useState(null);
  useEffect(() => {
    const p = prizeListImport;
    if (!p?.id || p.status !== 'extracted' || prizeParseBusy) return;
    const hasAI = p.parsed_data && p.parsed_data.version === 'ai-v1';
    if (!hasAI && autoAnalyzedId !== p.id) {
      setAutoAnalyzedId(p.id);
      setAiReadFailed(false);
      analyzePrizeList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prizeListImport, prizeParseBusy, autoAnalyzedId]);

  const loadEntryProfile = async () => {
    try {
      const { data: profile } = await api.get(
        '/show-center/entry-profile'
      );

      setEntryProfile(profile);
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        'Failed to load show entry profile'
      );
    }
  };

  const saveEntryProfile = async () => {
    if (!entryProfile) return;

    try {
      setEntryProfileSaving(true);

      const { data: saved } = await api.put(
        '/show-center/entry-profile',
        entryProfile
      );

      setEntryProfile(saved);
      toast.success('Show entry profile saved');
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        'Failed to save show entry profile'
      );
    } finally {
      setEntryProfileSaving(false);
    }
  };

  const toggleEntryHorse = horseId => {
    setEntryHorseIds(prev =>
      prev.includes(horseId)
        ? prev.filter(id => id !== horseId)
        : [...prev, horseId]
    );
  };

  const loadEntryDetail = async entryId => {
    if (!entryId) return;

    try {
      const { data } = await api.get(
        `/show-center/entries/${entryId}`
      );

      setEntryDetail(data);
      return data;
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        'Failed to load show entry'
      );
    }
  };

  const addEntryClass = async () => {
    const entryId =
      entryDetail?.entry?.id ||
      createdEntry?.entry?.id;

    if (!entryId) return;

    if (!entryClassForm.entry_horse_id) {
      return toast.error('Choose a horse');
    }

    if (!entryClassForm.class_name.trim()) {
      return toast.error('Enter a class name');
    }

    try {
      setEntryEditBusy(true);

      await api.post(
        `/show-center/entries/${entryId}/classes`,
        {
          ...entryClassForm,
          fee: Number(entryClassForm.fee || 0)
        }
      );

      setEntryClassForm(prev => ({
        ...prev,
        class_number: '',
        class_name: '',
        fee: ''
      }));

      await loadEntryDetail(entryId);
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        'Failed to add class'
      );
    } finally {
      setEntryEditBusy(false);
    }
  };

  const removeEntryClass = async classId => {
    const entryId =
      entryDetail?.entry?.id ||
      createdEntry?.entry?.id;

    try {
      setEntryEditBusy(true);

      await api.delete(
        `/show-center/entry-classes/${classId}`
      );

      await loadEntryDetail(entryId);
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        'Failed to remove class'
      );
    } finally {
      setEntryEditBusy(false);
    }
  };

  const addEntryFee = async () => {
    const entryId =
      entryDetail?.entry?.id ||
      createdEntry?.entry?.id;

    if (!entryId) return;

    if (!entryFeeForm.label.trim()) {
      return toast.error('Enter a fee name');
    }

    try {
      setEntryEditBusy(true);

      await api.post(
        `/show-center/entries/${entryId}/fees`,
        {
          ...entryFeeForm,
          entry_horse_id:
            entryFeeForm.scope === 'entry'
              ? null
              : entryFeeForm.entry_horse_id,
          amount: Number(entryFeeForm.amount || 0),
          quantity: Number(entryFeeForm.quantity || 1)
        }
      );

      setEntryFeeForm(prev => ({
        ...prev,
        label: '',
        amount: '',
        quantity: '1'
      }));

      await loadEntryDetail(entryId);
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        'Failed to add fee'
      );
    } finally {
      setEntryEditBusy(false);
    }
  };

  const removeEntryFee = async feeId => {
    const entryId =
      entryDetail?.entry?.id ||
      createdEntry?.entry?.id;

    try {
      setEntryEditBusy(true);

      await api.delete(
        `/show-center/entry-fees/${feeId}`
      );

      await loadEntryDetail(entryId);
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        'Failed to remove fee'
      );
    } finally {
      setEntryEditBusy(false);
    }
  };

  const openClassesFees = async () => {
    const entryId =
      entryDetail?.entry?.id ||
      createdEntry?.entry?.id;

    if (!entryId) return;

    const data = await loadEntryDetail(entryId);

    if (!data) return;

    const firstHorseId =
      data.horses?.[0]?.id || '';

    setEntryClassForm(prev => ({
      ...prev,
      entry_horse_id:
        prev.entry_horse_id || firstHorseId
    }));

    setEntryFeeForm(prev => ({
      ...prev,
      entry_horse_id:
        prev.entry_horse_id || firstHorseId
    }));

    setShowEntryMode('classes');
  };

  const createDraftEntry = async () => {
    if (!entryShowId) {
      return toast.error('Choose a show');
    }

    if (!entryHorseIds.length) {
      return toast.error('Choose at least one horse');
    }

    try {
      setEntryCreating(true);

      if (entryProfile) {
        const { data: savedProfile } = await api.put(
          '/show-center/entry-profile',
          entryProfile
        );

        setEntryProfile(savedProfile);
      }

      const { data: result } = await api.post(
        '/show-center/entries',
        {
          show_id: entryShowId,
          horse_ids: entryHorseIds
        }
      );

      setCreatedEntry(result);

      toast.success(
        `Draft entry created for ${result.horses?.length || 0} horse(s)`
      );
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        'Failed to create show entry'
      );
    } finally {
      setEntryCreating(false);
    }
  };

  const importPrizeList = async () => {
    if (!prizeListFile) return toast.error('Choose a prize list first');

    try {
      setPrizeListImporting(true);
      const fd = new FormData();
      fd.append('prize_list', prizeListFile);
      const { data: imported } = await api.post('/show-center/prize-lists/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPrizeListImport(imported);
      toast.success('Prize list uploaded — extracting text now');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to import prize list');
    } finally {
      setPrizeListImporting(false);
    }
  };

  const saveSecretary = async () => {
    try {
      if (!secretaryForm.name || !secretaryForm.email) return toast.error('Name and email are required');
      const resp = await api.post('/show-center/secretaries', secretaryForm);
      toast.success('Show secretary saved');
      setSecretaryForm({ name: '', email: '', phone: '', organization: '', notes: '' });
      await load();
      setPacketForm(prev => ({ ...prev, secretary_id: resp.data.id, to_email: resp.data.email }));
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save secretary'); }
  };

  const saveShow = async () => {
    try {
      if (!showForm.show_name) return toast.error('Show name is required');
      const resp = await api.post('/show-center/shows', showForm);
      toast.success('Show saved');
      setShowForm({ show_name: '', start_date: '', end_date: '', venue_name: '', city: '', state: '', secretary_id: '', exhibitor_name: '', entry_method: 'online_platform', status: 'preparing', notes: '', prize_list_import_id: '', entry_deadline: '', late_entry_deadline: '' });
      await load();
      setShowHorseForm(prev => ({ ...prev, show_id: resp.data.id }));
      setPacketForm(prev => ({ ...prev, show_id: resp.data.id, secretary_id: resp.data.secretary_id || '', subject: makePacketSubject(resp.data), body: makePacketBody(resp.data, []) }));
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save show'); }
  };

  const addHorseToShow = async () => {
    try {
      if (!showHorseForm.show_id || !showHorseForm.horse_id) return toast.error('Choose a show and horse');
      await api.post(`/show-center/shows/${showHorseForm.show_id}/horses`, showHorseForm);
      toast.success('Horse added to show');
      setShowHorseForm(prev => ({ ...prev, horse_id: '', rider_name: '', owner_name: '', trainer_name: '', class_list: '', stabling_notes: '', notes: '' }));
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to add horse to show'); }
  };

  const saveCredential = async (override = null) => {
    try {
      const form = override || credentialForm;
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v === undefined || v === null ? '' : v));
      if (form.data_json) fd.set('data_json', form.data_json);
      ['front', 'back', 'document'].forEach(role => {
        if (selectedFiles[role]) fd.append(role, selectedFiles[role]);
      });
      if (selectedFiles.files?.length) Array.from(selectedFiles.files).forEach(file => fd.append('files', file));
      await api.post('/show-center/credentials', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Credential saved');
      setSelectedFiles({});
      setCredentialForm({ applies_to: 'horse', horse_id: '', credential_type: 'USEF Member Card', organization: '', display_name: '', subject_name: '', id_number: '', status: 'active', issue_date: '', expiration_date: '', notes: '', needs_front_back: true });
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save credential'); }
  };

  const saveUsefVaccine = async () => {
    const horse = horses.find(h => h.id === vaccineForm.horse_id);
    const dataJson = JSON.stringify({
      usef_form_workflow: true,
      horse_name: horse?.name || vaccineForm.horse_name_text,
      owner_name: vaccineForm.owner_name,
      flu: { date: vaccineForm.flu_date, vaccine_name: vaccineForm.flu_name, serial_or_lot: vaccineForm.flu_serial, vaccine_expiration: vaccineForm.flu_expiration },
      ehv_rhino: { date: vaccineForm.ehv_date, vaccine_name: vaccineForm.ehv_name, serial_or_lot: vaccineForm.ehv_serial, vaccine_expiration: vaccineForm.ehv_expiration },
      veterinarian_name: vaccineForm.veterinarian_name,
      clinic_name: vaccineForm.clinic_name,
      vet_signature_present: vaccineForm.vet_signature_present,
      owner_signature_present: vaccineForm.owner_signature_present,
      date_signed: vaccineForm.date_signed,
      notes: vaccineForm.notes
    });
    await saveCredential({
      applies_to: 'horse',
      horse_id: vaccineForm.horse_id,
      credential_type: 'USEF Equine Vaccination Record',
      organization: 'USEF',
      display_name: `USEF Vaccine Record${horse?.name ? ` - ${horse.name}` : ''}`,
      subject_name: horse?.name || vaccineForm.horse_name_text,
      id_number: '',
      status: vaccineForm.vet_signature_present || vaccineForm.owner_signature_present ? 'signed' : 'draft_needs_signature',
      issue_date: vaccineForm.date_signed || vaccineForm.flu_date || vaccineForm.ehv_date,
      expiration_date: '',
      notes: vaccineForm.notes,
      needs_front_back: false,
      data_json: dataJson
    });
  };

  const onSelectShowForPacket = (showId) => {
    const show = shows.find(s => s.id === showId);
    const sec = secretaries.find(x => x.id === show?.secretary_id);
    setPacketForm(prev => ({
      ...prev,
      show_id: showId,
      secretary_id: show?.secretary_id || prev.secretary_id,
      to_email: sec?.email || show?.secretary_email || prev.to_email,
      subject: makePacketSubject(show),
      body: makePacketBody(show, [])
    }));
  };

  const onSelectSecretaryForPacket = (secretaryId) => {
    const sec = secretaries.find(s => s.id === secretaryId);
    setPacketForm(prev => ({ ...prev, secretary_id: secretaryId, to_email: sec?.email || prev.to_email }));
  };

  const toggleFile = (fileId) => {
    setPacketForm(prev => {
      const exists = prev.credential_file_ids.includes(fileId);
      return { ...prev, credential_file_ids: exists ? prev.credential_file_ids.filter(id => id !== fileId) : [...prev.credential_file_ids, fileId] };
    });
  };

  const sendPacket = async () => {
    try {
      if (!packetForm.show_id) return toast.error('Choose a show');
      if (!packetForm.to_email) return toast.error('Choose or enter a secretary email');
      const selected = credentials.filter(c => (c.files || []).some(f => packetForm.credential_file_ids.includes(f.id)));
      const payload = { ...packetForm, body: packetForm.body || makePacketBody(selectedShow, selected) };
      const resp = await api.post(`/show-center/shows/${packetForm.show_id}/send-packet`, payload);
      if (resp.data.status === 'sent') toast.success('Packet sent to secretary');
      else toast(resp.data.message || 'Packet saved; email not sent');
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to send packet'); }
  };

  const setPrizeShowValue = (fieldName, value) => {
    setPrizeReview(prev => {
      if (!prev) return prev;

      const current = prev.show?.[fieldName];

      const nextField =
        current &&
        typeof current === 'object' &&
        !Array.isArray(current)
          ? { ...current, value }
          : {
              value,
              page: null,
              source_text: ''
            };

      return {
        ...prev,
        show: {
          ...(prev.show || {}),
          [fieldName]: nextField
        }
      };
    });
  };

  const prizeSourceHref = page => {
    if (!prizeListImport?.file_url || !page) return null;

    return `${prizeListImport.file_url}#page=${page}`;
  };

  const filteredPrizeClasses = useMemo(() => {
    const classes = prizeReview?.classes || [];
    const search = prizeClassSearch
      .trim()
      .toLowerCase();

    if (!search) return classes;

    return classes.filter(item => {
      const values = [
        item.class_number,
        item.aha_number,
        item.class_name,
        item.judge,
        item.day,
        item.session
      ];

      return values.some(value =>
        String(value || '')
          .toLowerCase()
          .includes(search)
      );
    });
  }, [prizeReview, prizeClassSearch]);

  const showMissingWarnings = useMemo(() => {
    const warnings = [];
    const names = credentials.map(c => `${c.credential_type} ${c.display_name}`.toLowerCase());
    if (!names.some(n => n.includes('coggins'))) warnings.push('No Coggins credential uploaded yet.');
    if (!names.some(n => n.includes('vaccination'))) warnings.push('No USEF vaccine form uploaded or built yet.');
    if (!names.some(n => n.includes('registration'))) warnings.push('No registration papers uploaded yet.');
    return warnings;
  }, [credentials]);

  return (
    <div>
      <TopBar title="Show Center" />
      <div className="page-content" style={{ paddingBottom: 90 }}>
        {loading && <div className="loading-screen"><div className="spinner" /></div>}
        {!loading && (
          <>
            {activeTab !== 'Home' && (
              <button className="btn btn-sm btn-secondary" style={{ marginBottom: 12 }} onClick={() => setActiveTab('Home')}>&#8592; Show Center</button>
            )}

            {activeTab === 'Home' && <ShowCenterHub onOpen={setActiveTab} />}

            {activeTab === 'Shows' && (
              <>
                <Card style={{ borderLeft: '3px solid var(--cr-green)', borderRadius: '0 var(--radius-lg) var(--radius-lg) 0' }}>
                  <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Universal show-entry packet builder</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Store credentials once, bundle front/back card images, and send clean packets to any show secretary regardless of platform.</div>
                  {showMissingWarnings.length > 0 && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--cr-amber)' }}>{showMissingWarnings.map(w => <div key={w}>• {w}</div>)}</div>}
                </Card>

                <div className="section-label">Add show</div>

                {showEntryMode === 'chooser' && (
                  <Card>
                    <button
                      className="btn btn-primary btn-full"
                      onClick={openPrizeImporter}
                      style={{ minHeight: 50, marginBottom: 10 }}
                    >
                      Import Prize List
                    </button>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', marginBottom: 10 }}>
                      PDF, scanned PDF, JPG, PNG, or WEBP
                    </div>
                    <button className="btn btn-secondary btn-full" onClick={() => setShowEntryMode('manual')}>
                      Enter show manually
                    </button>

                    {shows.length > 0 && (
                      <button
                        className="btn btn-secondary btn-full"
                        style={{ marginTop: 10 }}
                        onClick={() => {
                          setShowEntryMode('entry');
                          setEntryShowId(shows[0]?.id || '');
                          setEntryHorseIds([]);
                          setCreatedEntry(null);
                          loadEntryProfile();
                        }}
                      >
                        Build Show Entry
                      </button>
                    )}
                  </Card>
                )}

                {showEntryMode === 'import' && (
                  <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 800 }}>Import prize list</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          We extract native PDF text first and OCR only the pages that need it.
                        </div>
                      </div>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => {
                          setShowEntryMode('chooser');
                          setPrizeListFile(null);
                          setPrizeListImport(null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>

                    {!prizeListImport && (
                      <>
                        <Field label="Prize list">
                          <input
                            type="file"
                            className="form-input"
                            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                            onChange={e => setPrizeListFile(e.target.files?.[0] || null)}
                          />
                        </Field>
                        {prizeListFile && (
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '-2px 0 10px' }}>
                            {prizeListFile.name} · {(prizeListFile.size / 1024 / 1024).toFixed(1)} MB
                          </div>
                        )}
                        <button
                          className="btn btn-primary btn-full"
                          onClick={importPrizeList}
                          disabled={!prizeListFile || prizeListImporting}
                        >
                          {prizeListImporting ? 'Uploading…' : 'Upload & Extract Prize List'}
                        </button>

                        {recentPrizeImports.length > 0 && (
                          <>
                            <div
                              className="section-label"
                              style={{ marginTop: 18 }}
                            >
                              Recent prize lists
                            </div>

                            {recentPrizeImports.map(item => (
                              <button
                                key={item.id}
                                type="button"
                                className="btn btn-secondary btn-full"
                                onClick={() => resumePrizeListImport(item.id)}
                                style={{
                                  textAlign: 'left',
                                  marginBottom: 8,
                                  padding: 12,
                                  width: '100%',
                                  minWidth: 0,
                                  overflow: 'hidden'
                                }}
                              >
                                <div
                                  title={item.file_name}
                                  style={{
                                    fontWeight: 800,
                                    fontSize: 12,
                                    width: '100%',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {item.file_name}
                                </div>

                                <div
                                  style={{
                                    fontSize: 10,
                                    color: 'var(--text-muted)',
                                    marginTop: 4,
                                    whiteSpace: 'normal',
                                    overflowWrap: 'anywhere'
                                  }}
                                >
                                  {item.page_count || 1} pages
                                  {' · '}
                                  OCR {item.ocr_pages || 0}
                                  {' · '}
                                  {Number(item.extracted_char_count || 0).toLocaleString()} characters
                                </div>
                              </button>
                            ))}
                          </>
                        )}
                      </>
                    )}

                    {prizeListImport && ['processing', 'queued'].includes(prizeListImport.status) && (
                      <div style={{ padding: '14px 0' }}>
                        <div style={{ fontWeight: 700 }}>Step 1 of 2 · Extracting pages from the PDF</div>
                        <progress style={{ width: '100%', height: 8, marginTop: 8 }} />
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                          Large scanned prize lists can take a few minutes.
                        </div>
                        <div style={{ background: 'var(--cr-amber-light)', border: '1px solid var(--cr-amber)', borderRadius: 8, padding: '6px 10px', fontSize: 11, color: 'var(--cr-amber)', marginTop: 8 }}>
                          Please don't close or leave this page while we read your prize list.
                        </div>
                      </div>
                    )}

                    {prizeListImport?.status === 'failed' && (
                      <div style={{ border: '1px solid #E4A0A0', background: '#FFF4F4', borderRadius: 10, padding: 12 }}>
                        <div style={{ fontWeight: 800, marginBottom: 4 }}>Import failed</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{prizeListImport.error_message || 'The prize list could not be read.'}</div>
                        <button
                          className="btn btn-secondary btn-full"
                          style={{ marginTop: 10 }}
                          onClick={() => {
                            setPrizeListImport(null);
                            setPrizeListFile(null);
                          }}
                        >
                          Try another file
                        </button>
                      </div>
                    )}

                    {prizeListImport?.status === 'extracted' && (
                      <>
                        <div style={{ border: '1px solid var(--border-color)', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                          <div style={{ fontWeight: 800 }}>Prize list extracted</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{prizeListImport.file_name}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            <span className="badge badge-green">{prizeListImport.page_count || 1} page{prizeListImport.page_count === 1 ? '' : 's'}</span>
                            <span className="badge">{prizeListImport.extraction_method || 'extracted'}</span>
                            {prizeListImport.ocr_pages > 0 && <span className="badge">OCR: {prizeListImport.ocr_pages} page{prizeListImport.ocr_pages === 1 ? '' : 's'}</span>}
                            <span className="badge">{Number(prizeListImport.extracted_char_count || 0).toLocaleString()} characters</span>
                          </div>
                          {prizeListImport.file_url && (
                            <a href={prizeListImport.file_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 12 }}>
                              View original prize list
                            </a>
                          )}
                        </div>

                        <button
                          className="btn btn-primary btn-full"
                          style={{ marginTop: 10 }}
                          disabled={prizeParseBusy}
                          onClick={async () => {
                            const ai = prizeListImport.parsed_data || {};
                            const v = k => (ai[k] && ai[k].value) || '';
                            const fallbackName = (prizeListImport.file_name || 'New Show')
                              .replace(/\.[^.]+$/, '')
                              .replace(/[_-]+/g, ' ');
                            const sec = ai.secretary || {};
                            let secretaryId = '';
                            if (sec.email) {
                              const existing = secretaries.find(s => (s.email || '').toLowerCase() === sec.email.toLowerCase());
                              if (existing) {
                                secretaryId = existing.id;
                              } else {
                                try {
                                  const { data: created } = await api.post('/show-center/secretaries', { name: sec.name || sec.email, email: sec.email, phone: sec.phone || undefined });
                                  setData(prev => ({ ...prev, secretaries: [...(prev.secretaries || []), created] }));
                                  secretaryId = created.id;
                                  toast.success('Secretary ' + (created.name || sec.email) + ' added to your contacts');
                                } catch (secErr) {
                                  toast.error('Could not save secretary automatically');
                                }
                              }
                            }
                            const noteLines = [];
                            if (sec.name) noteLines.push('Secretary: ' + sec.name + (sec.email ? ' (' + sec.email + ')' : ''));
                            setShowForm(prev => ({
                              ...prev,
                              show_name: v('show_name') || (ai.version === 'ai-v1' ? fallbackName : ''),
                              start_date: v('start_date'),
                              end_date: v('end_date'),
                              entry_deadline: v('entry_deadline'),
                              late_entry_deadline: v('late_entry_deadline'),
                              venue_name: v('venue_name'),
                              city: v('city'),
                              state: v('state'),
                              secretary_id: secretaryId || prev.secretary_id,
                              entry_method: (ai.entry_method && ['mail', 'email', 'online_platform'].includes(ai.entry_method.value)) ? ai.entry_method.value : prev.entry_method,
                              notes: noteLines.join('\n'),
                              prize_list_import_id: prizeListImport.id
                            }));
                            setShowEntryMode('manual');
                            if (ai.version === 'ai-v1') toast.success('Show details filled from the prize list - review before saving');
                          }}
                        >
                          {prizeParseBusy ? 'Reading show details…' : ((aiReadFailed && prizeListImport?.parsed_data?.version !== 'ai-v1') ? 'Skip — fill the form manually' : 'Continue to Show Setup')}
                        </button>

                        <div
                          style={{
                            marginTop: 10,
                            padding: 10,
                            borderRadius: 10,
                            background: 'var(--bg-secondary)',
                            fontSize: 11,
                            color: 'var(--text-secondary)'
                          }}
                        >
                          Text extraction complete. Native PDF text is preserved whenever available, with OCR used only as a fallback for pages that need it.
                        </div>

                        <div
                          style={{
                            fontSize: 10,
                            color: 'var(--text-muted)',
                            marginTop: 8
                          }}
                        >
                          {prizeParseBusy ? (
                            <span style={{ display: 'block' }}>
                              <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-primary)' }}>Step 2 of 2 · Reading show details</span>
                              <progress style={{ width: '100%', height: 8, margin: '6px 0 2px' }} />
                              <span style={{ display: 'block', fontSize: 11, color: 'var(--cr-amber)' }}>{aiBusyMessage}</span>
                              <span style={{ display: 'block', background: 'var(--cr-amber-light)', border: '1px solid var(--cr-amber)', borderRadius: 8, padding: '6px 10px', fontSize: 11, color: 'var(--cr-amber)', marginTop: 6 }}>
                                Please don't close or leave this page.
                              </span>
                            </span>
                          ) : prizeListImport?.parsed_data?.version === 'ai-v1' ? (
                            <span style={{ display: 'block', background: 'var(--cr-green-light, #e8f5ee)', border: '1px solid var(--cr-green)', borderRadius: 8, padding: '8px 10px' }}>
                              <span style={{ display: 'block', fontWeight: 700, fontSize: 12, color: 'var(--cr-green)' }}>
                                {'\u2713 Found: ' + (prizeListImport.parsed_data.show_name?.value || 'show details')}
                              </span>
                              <span style={{ display: 'block', fontSize: 11, color: 'var(--cr-green)', marginTop: 2 }}>
                                {(prizeListImport.parsed_data.start_date?.value ? 'Starts ' + prizeListImport.parsed_data.start_date.value : '')
                                  + (prizeListImport.parsed_data.entry_deadline?.value ? ' \u00b7 entries close ' + prizeListImport.parsed_data.entry_deadline.value : '')}
                              </span>
                            </span>
                          ) : aiReadFailed ? (
                            <span style={{ display: 'block', background: 'var(--cr-red-light, #FCEBEB)', border: '1px solid var(--cr-red)', borderRadius: 8, padding: '8px 10px' }}>
                              <span style={{ display: 'block', fontWeight: 700, fontSize: 12, color: 'var(--cr-red)' }}>{'\u2715 Couldn\u2019t read this prize list'}</span>
                              <span style={{ display: 'block', fontSize: 11, color: 'var(--cr-red)', marginTop: 2 }}>The reading service is overloaded right now.</span>
                              <button type="button" className="btn btn-sm btn-primary" style={{ marginTop: 8 }} onClick={analyzePrizeList}>
                                Try reading again
                              </button>
                            </span>
                          ) : 'Preparing to read show details…'}
                        </div>

                        <button
                          className="btn btn-secondary btn-full"
                          style={{ marginTop: 10 }}
                          onClick={() => {
                            setPrizeListImport(null);
                            setPrizeListFile(null);
                          }}
                        >
                          Import another prize list
                        </button>
                      </>
                    )}
                  </Card>
                )}

                
{showEntryMode === 'entry' && (
  <div style={{ display: 'grid', gap: 16 }}>

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12
      }}
    >
      <div>
        <h3 style={{ margin: 0 }}>
          Build Show Entry
        </h3>

        <div
          style={{
            marginTop: 4,
            fontSize: 13,
            color: '#667085'
          }}
        >
          Choose the show and horses you are entering.
        </div>
      </div>

      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => {
          setShowEntryMode('chooser');
          setCreatedEntry(null);
        }}
      >
        Back
      </button>
    </div>


    <div className="card">
      <div style={{ fontWeight: 800, marginBottom: 10 }}>
        1. Show
      </div>

      <select
        value={entryShowId}
        onChange={e => {
          setEntryShowId(e.target.value);
          setCreatedEntry(null);
        }}
        style={{ width: '100%' }}
      >
        <option value="">
          Choose a saved show
        </option>

        {shows.map(show => (
          <option
            key={show.id}
            value={show.id}
          >
            {show.show_name}
            {show.start_date
              ? ` — ${new Date(
                  `${show.start_date}T12:00:00`
                ).toLocaleDateString()}`
              : ''}
          </option>
        ))}
      </select>
    </div>


    <div className="card">
      <div style={{ fontWeight: 800, marginBottom: 4 }}>
        2. Exhibitor Information
      </div>

      <div
        style={{
          fontSize: 12,
          color: '#667085',
          marginBottom: 12
        }}
      >
        This information is saved for future show entries.
      </div>

      {!entryProfile ? (
        <div style={{ color: '#667085' }}>
          Loading exhibitor information...
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(190px, 1fr))',
            gap: 10
          }}
        >
          <input
            placeholder="First name"
            value={entryProfile.first_name || ''}
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                first_name: e.target.value
              }))
            }
          />

          <input
            placeholder="Last name"
            value={entryProfile.last_name || ''}
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                last_name: e.target.value
              }))
            }
          />

          <input
            placeholder="Email"
            type="email"
            value={entryProfile.email || ''}
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                email: e.target.value
              }))
            }
          />

          <input
            placeholder="Phone"
            value={entryProfile.phone || ''}
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                phone: e.target.value
              }))
            }
          />

          <input
            placeholder="Address"
            value={entryProfile.address_line_1 || ''}
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                address_line_1: e.target.value
              }))
            }
          />

          <input
            placeholder="Address line 2"
            value={entryProfile.address_line_2 || ''}
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                address_line_2: e.target.value
              }))
            }
          />

          <input
            placeholder="City"
            value={entryProfile.city || ''}
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                city: e.target.value
              }))
            }
          />

          <input
            placeholder="State"
            value={entryProfile.state || ''}
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                state: e.target.value
              }))
            }
          />

          <input
            placeholder="ZIP / Postal Code"
            value={entryProfile.postal_code || ''}
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                postal_code: e.target.value
              }))
            }
          />

          <input
            placeholder="Emergency contact"
            value={
              entryProfile.emergency_contact_name || ''
            }
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                emergency_contact_name:
                  e.target.value
              }))
            }
          />

          <input
            placeholder="Emergency contact phone"
            value={
              entryProfile.emergency_contact_phone || ''
            }
            onChange={e =>
              setEntryProfile(prev => ({
                ...prev,
                emergency_contact_phone:
                  e.target.value
              }))
            }
          />
        </div>
      )}

      {entryProfile && (
        <button
          type="button"
          className="btn btn-secondary"
          style={{ marginTop: 12 }}
          disabled={entryProfileSaving}
          onClick={saveEntryProfile}
        >
          {entryProfileSaving
            ? 'Saving...'
            : 'Save Exhibitor Information'}
        </button>
      )}
    </div>


    <div className="card">
      <div style={{ fontWeight: 800, marginBottom: 4 }}>
        3. Horses
      </div>

      <div
        style={{
          fontSize: 12,
          color: '#667085',
          marginBottom: 12
        }}
      >
        Select every horse being entered in this show.
      </div>

      {!horses.length ? (
        <div style={{ color: '#667085' }}>
          No horses are available.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {horses.map(horse => {
            const selected =
              entryHorseIds.includes(horse.id);

            return (
              <label
                key={horse.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: 12,
                  border: selected
                    ? '2px solid #667085'
                    : '1px solid #d0d5dd',
                  borderRadius: 10,
                  cursor: 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() =>
                    toggleEntryHorse(horse.id)
                  }
                  style={{ marginTop: 3 }}
                />

                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>
                    {horse.name}
                  </div>

                  <div
                    style={{
                      marginTop: 3,
                      fontSize: 12,
                      color: '#667085'
                    }}
                  >
                    {[
                      horse.registration_number
                        ? `Reg. ${horse.registration_number}`
                        : null,
                      horse.breed || null,
                      horse.color || null,
                      horse.date_of_birth
                        ? `DOB ${new Date(
                            `${horse.date_of_birth}T12:00:00`
                          ).toLocaleDateString()}`
                        : null
                    ]
                      .filter(Boolean)
                      .join(' • ') || 'Horse profile'}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>


    {!createdEntry ? (
      <button
        type="button"
        className="btn btn-primary btn-full"
        disabled={
          entryCreating ||
          !entryShowId ||
          !entryHorseIds.length ||
          !entryProfile
        }
        onClick={createDraftEntry}
      >
        {entryCreating
          ? 'Creating Draft Entry...'
          : `Create Draft Entry${
              entryHorseIds.length
                ? ` (${entryHorseIds.length} horse${
                    entryHorseIds.length === 1 ? '' : 's'
                  })`
                : ''
            }`}
      </button>
    ) : (
      <div className="card">
        <div
          style={{
            fontSize: 18,
            fontWeight: 900,
            marginBottom: 4
          }}
        >
          Draft Entry Created
        </div>

        <div
          style={{
            fontSize: 13,
            color: '#667085',
            marginBottom: 14
          }}
        >
          {createdEntry.horses?.length || 0}
          {' '}
          horse
          {createdEntry.horses?.length === 1
            ? ''
            : 's'}
          {' '}
          added to this entry.
        </div>

        <div
          style={{
            padding: 12,
            border: '1px solid #e4e7ec',
            borderRadius: 10,
            marginBottom: 12
          }}
        >
          <div
            style={{
              fontWeight: 800,
              marginBottom: 6
            }}
          >
            Documents Found
          </div>

          {!createdEntry.documents?.length ? (
            <div
              style={{
                fontSize: 13,
                color: '#b54708'
              }}
            >
              No matching show documents were found yet.
              You can add them before sending the entry.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 6 }}>
              {createdEntry.documents.map(doc => (
                <div
                  key={doc.id}
                  style={{
                    fontSize: 13,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10
                  }}
                >
                  <span>
                    <strong>
                      {doc.document_type}
                    </strong>
                    {doc.label &&
                    doc.label !== doc.document_type
                      ? ` — ${doc.label}`
                      : ''}
                  </span>

                  <span
                    style={{
                      color:
                        doc.validation_status === 'ready'
                          ? '#027a48'
                          : '#b54708',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {doc.validation_status === 'ready'
                      ? 'Ready'
                      : 'Review'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          className="btn btn-primary btn-full"
          onClick={openClassesFees}
        >
          Continue to Classes & Fees
        </button>
      </div>
    )}

  </div>
)}


{showEntryMode === 'classes' && entryDetail && (
  <div style={{ display: 'grid', gap: 16 }}>

    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12
      }}
    >
      <div>
        <h3 style={{ margin: 0 }}>
          Classes & Fees
        </h3>

        <div
          style={{
            marginTop: 4,
            fontSize: 13,
            color: '#667085'
          }}
        >
          {entryDetail.entry.show_name}
        </div>
      </div>

      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => setShowEntryMode('entry')}
      >
        Back
      </button>
    </div>


    <div className="card">
      <div
        style={{
          fontSize: 16,
          fontWeight: 900,
          marginBottom: 12
        }}
      >
        Add Class
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10
        }}
      >
        <select
          value={entryClassForm.entry_horse_id}
          onChange={e =>
            setEntryClassForm(prev => ({
              ...prev,
              entry_horse_id: e.target.value
            }))
          }
        >
          <option value="">
            Choose horse
          </option>

          {entryDetail.horses.map(horse => (
            <option
              key={horse.id}
              value={horse.id}
            >
              {horse.horse_snapshot?.name ||
                'Horse'}
            </option>
          ))}
        </select>

        <input
          placeholder="Class #"
          value={entryClassForm.class_number}
          onChange={e =>
            setEntryClassForm(prev => ({
              ...prev,
              class_number: e.target.value
            }))
          }
        />

        <input
          placeholder="Class name"
          value={entryClassForm.class_name}
          onChange={e =>
            setEntryClassForm(prev => ({
              ...prev,
              class_name: e.target.value
            }))
          }
        />

        <input
          type="number"
          min="0"
          step="0.01"
          placeholder="Entry fee"
          value={entryClassForm.fee}
          onChange={e =>
            setEntryClassForm(prev => ({
              ...prev,
              fee: e.target.value
            }))
          }
        />
      </div>

      <button
        type="button"
        className="btn btn-primary"
        style={{ marginTop: 10 }}
        disabled={entryEditBusy}
        onClick={addEntryClass}
      >
        Add Class
      </button>
    </div>


    {entryDetail.horses.map(horse => (
      <div
        className="card"
        key={horse.id}
      >
        <div
          style={{
            fontSize: 16,
            fontWeight: 900,
            marginBottom: 4
          }}
        >
          {horse.horse_snapshot?.name ||
            'Horse'}
        </div>

        <div
          style={{
            fontSize: 12,
            color: '#667085',
            marginBottom: 12
          }}
        >
          {[
            horse.horse_snapshot?.registration_number
              ? `Reg. ${horse.horse_snapshot.registration_number}`
              : null,
            horse.horse_snapshot?.breed || null
          ]
            .filter(Boolean)
            .join(' • ')}
        </div>

        {!horse.classes?.length ? (
          <div
            style={{
              fontSize: 13,
              color: '#667085'
            }}
          >
            No classes selected yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {horse.classes.map(item => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  padding: 10,
                  border: '1px solid #e4e7ec',
                  borderRadius: 8
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>
                    {item.class_number
                      ? `#${item.class_number} — `
                      : ''}
                    {item.class_name}
                  </div>

                  <div
                    style={{
                      marginTop: 2,
                      fontSize: 12,
                      color: '#667085'
                    }}
                  >
                    ${Number(
                      item.fee || 0
                    ).toFixed(2)}
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={entryEditBusy}
                  onClick={() =>
                    removeEntryClass(item.id)
                  }
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    ))}


    <div className="card">
      <div
        style={{
          fontSize: 16,
          fontWeight: 900,
          marginBottom: 4
        }}
      >
        Additional Fees
      </div>

      <div
        style={{
          fontSize: 12,
          color: '#667085',
          marginBottom: 12
        }}
      >
        Add office, drug, grounds, stall,
        shavings, post-entry or other fees.
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 10
        }}
      >
        <select
          value={entryFeeForm.scope}
          onChange={e =>
            setEntryFeeForm(prev => ({
              ...prev,
              scope: e.target.value
            }))
          }
        >
          <option value="entry">
            Entire entry — charge once
          </option>

          <option value="horse">
            Specific horse
          </option>
        </select>

        {entryFeeForm.scope === 'horse' && (
          <select
            value={entryFeeForm.entry_horse_id}
            onChange={e =>
              setEntryFeeForm(prev => ({
                ...prev,
                entry_horse_id: e.target.value
              }))
            }
          >
            <option value="">
              Choose horse
            </option>

            {entryDetail.horses.map(horse => (
              <option
                key={horse.id}
                value={horse.id}
              >
                {horse.horse_snapshot?.name ||
                  'Horse'}
              </option>
            ))}
          </select>
        )}

        <input
          placeholder="Fee name"
          value={entryFeeForm.label}
          onChange={e =>
            setEntryFeeForm(prev => ({
              ...prev,
              label: e.target.value
            }))
          }
        />

        <input
          type="number"
          min="0"
          step="0.01"
          placeholder="Amount"
          value={entryFeeForm.amount}
          onChange={e =>
            setEntryFeeForm(prev => ({
              ...prev,
              amount: e.target.value
            }))
          }
        />

        <input
          type="number"
          min="1"
          step="1"
          placeholder="Qty"
          value={entryFeeForm.quantity}
          onChange={e =>
            setEntryFeeForm(prev => ({
              ...prev,
              quantity: e.target.value
            }))
          }
        />
      </div>

      <button
        type="button"
        className="btn btn-primary"
        style={{ marginTop: 10 }}
        disabled={
          entryEditBusy ||
          (
            entryFeeForm.scope === 'horse' &&
            !entryFeeForm.entry_horse_id
          )
        }
        onClick={addEntryFee}
      >
        Add Fee
      </button>


      {!!entryDetail.entry_fees?.length && (
        <div
          style={{
            marginTop: 16,
            display: 'grid',
            gap: 8
          }}
        >
          <div style={{ fontWeight: 800 }}>
            Shared Entry Fees
          </div>

          {entryDetail.entry_fees.map(item => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 10,
                padding: 10,
                border: '1px solid #e4e7ec',
                borderRadius: 8
              }}
            >
              <span>
                {item.label}
                {' — '}
                ${Number(
                  item.amount || 0
                ).toFixed(2)}
                {Number(item.quantity || 1) > 1
                  ? ` × ${item.quantity}`
                  : ''}
              </span>

              <button
                type="button"
                className="btn btn-secondary"
                disabled={entryEditBusy}
                onClick={() =>
                  removeEntryFee(item.id)
                }
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}


      {entryDetail.horses.some(
        horse => horse.fees?.length
      ) && (
        <div
          style={{
            marginTop: 16,
            display: 'grid',
            gap: 12
          }}
        >
          <div style={{ fontWeight: 800 }}>
            Horse-Specific Fees
          </div>

          {entryDetail.horses.map(horse =>
            horse.fees?.length ? (
              <div key={horse.id}>
                <div
                  style={{
                    fontWeight: 700,
                    marginBottom: 6
                  }}
                >
                  {horse.horse_snapshot?.name ||
                    'Horse'}
                </div>

                <div style={{ display: 'grid', gap: 6 }}>
                  {horse.fees.map(item => (
                    <div
                      key={item.id}
                      style={{
                        display: 'flex',
                        justifyContent:
                          'space-between',
                        gap: 10,
                        padding: 10,
                        border:
                          '1px solid #e4e7ec',
                        borderRadius: 8
                      }}
                    >
                      <span>
                        {item.label}
                        {' — '}
                        ${Number(
                          item.amount || 0
                        ).toFixed(2)}
                        {Number(
                          item.quantity || 1
                        ) > 1
                          ? ` × ${item.quantity}`
                          : ''}
                      </span>

                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={entryEditBusy}
                        onClick={() =>
                          removeEntryFee(item.id)
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>
      )}
    </div>


    <div className="card">
      <div
        style={{
          fontSize: 18,
          fontWeight: 900,
          marginBottom: 12
        }}
      >
        Entry Total
      </div>

      <div
        style={{
          display: 'grid',
          gap: 7,
          fontSize: 14
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <span>Class fees</span>
          <strong>
            ${Number(
              entryDetail.entry.class_fee_total || 0
            ).toFixed(2)}
          </strong>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <span>Other fees</span>
          <strong>
            ${Number(
              entryDetail.entry.additional_fee_total || 0
            ).toFixed(2)}
          </strong>
        </div>

        <div
          style={{
            borderTop: '1px solid #d0d5dd',
            paddingTop: 10,
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 17
          }}
        >
          <strong>Total</strong>

          <strong>
            ${Number(
              entryDetail.entry.calculated_total || 0
            ).toFixed(2)}
          </strong>
        </div>
      </div>
    </div>


    <button
      type="button"
      className="btn btn-primary btn-full"
      onClick={() =>
        toast.success(
          'Classes and fees saved. Documents are next.'
        )
      }
    >
      Continue to Documents
    </button>

  </div>
)}

{showEntryMode === 'manual' && (
                  <Card>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <button className="btn btn-sm btn-secondary" onClick={() => setShowEntryMode('chooser')}>Cancel</button>
                    </div>
                    <Field label="Show name"><input className="form-input" value={showForm.show_name} onChange={e => update(setShowForm, 'show_name', e.target.value)} placeholder="Region 9 Sport Horse Championship" /></Field>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Field label="Start date"><input type="date" className="form-input" value={showForm.start_date} onChange={e => update(setShowForm, 'start_date', e.target.value)} /></Field>
                      <Field label="End date"><input type="date" className="form-input" value={showForm.end_date} onChange={e => update(setShowForm, 'end_date', e.target.value)} /></Field>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Field label="Entry deadline"><input type="date" className="form-input" value={showForm.entry_deadline} onChange={e => update(setShowForm, 'entry_deadline', e.target.value)} /></Field>
                      <Field label="Late entry deadline"><input type="date" className="form-input" value={showForm.late_entry_deadline} onChange={e => update(setShowForm, 'late_entry_deadline', e.target.value)} /></Field>
                    </div>
                    <Field label="Venue"><input className="form-input" value={showForm.venue_name} onChange={e => update(setShowForm, 'venue_name', e.target.value)} /></Field>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: 8 }}>
                      <Field label="City"><input className="form-input" value={showForm.city} onChange={e => update(setShowForm, 'city', e.target.value)} /></Field>
                      <Field label="State"><input className="form-input" value={showForm.state} onChange={e => update(setShowForm, 'state', e.target.value)} /></Field>
                    </div>
                    <Field label="Show secretary">
                      <select className="form-input" value={showForm.secretary_id} onChange={e => update(setShowForm, 'secretary_id', e.target.value)}>
                        <option value="">Select secretary</option>
                        {secretaries.map(s => <option key={s.id} value={s.id}>{s.name} · {s.email}</option>)}
                      </select>
                    </Field>
                    <Field label="Exhibitor name"><input className="form-input" value={showForm.exhibitor_name} onChange={e => update(setShowForm, 'exhibitor_name', e.target.value)} /></Field>
                    <Field label="Entry method">
                      <select className="form-input" value={showForm.entry_method} onChange={e => update(setShowForm, 'entry_method', e.target.value)}>
                        <option value="online_platform">Online platform</option><option value="email_pdf">PDF/email</option><option value="paper">Paper</option><option value="unknown">Not sure yet</option>
                      </select>
                    </Field>
                    <Field label="Notes"><textarea className="form-input" rows="3" value={showForm.notes} onChange={e => update(setShowForm, 'notes', e.target.value)} /></Field>
                    <button className="btn btn-primary" onClick={saveShow}>Save show</button>
                  </Card>
                )}

                {shows.length > 0 && <>
                  <div className="section-label">Add horse / rider / classes to show</div>
                  <Card>
                    <Field label="Show"><select className="form-input" value={showHorseForm.show_id} onChange={e => update(setShowHorseForm, 'show_id', e.target.value)}>{shows.map(s => <option key={s.id} value={s.id}>{s.show_name}</option>)}</select></Field>
                    <Field label="Horse"><select className="form-input" value={showHorseForm.horse_id} onChange={e => update(setShowHorseForm, 'horse_id', e.target.value)}><option value="">Select horse</option>{horses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}</select></Field>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Field label="Rider"><input className="form-input" value={showHorseForm.rider_name} onChange={e => update(setShowHorseForm, 'rider_name', e.target.value)} /></Field>
                      <Field label="Owner"><input className="form-input" value={showHorseForm.owner_name} onChange={e => update(setShowHorseForm, 'owner_name', e.target.value)} /></Field>
                    </div>
                    <Field label="Trainer"><input className="form-input" value={showHorseForm.trainer_name} onChange={e => update(setShowHorseForm, 'trainer_name', e.target.value)} /></Field>
                    <Field label="Classes / divisions"><textarea className="form-input" rows="3" value={showHorseForm.class_list} onChange={e => update(setShowHorseForm, 'class_list', e.target.value)} placeholder="Training Level Test 1, Sport Horse Mares In-Hand..." /></Field>
                    <Field label="Stabling notes"><textarea className="form-input" rows="2" value={showHorseForm.stabling_notes} onChange={e => update(setShowHorseForm, 'stabling_notes', e.target.value)} /></Field>
                    <button className="btn btn-primary" onClick={addHorseToShow}>Add to show</button>
                  </Card>
                </>}

                <div className="section-label">Saved shows</div>
                {shows.length === 0 && <Card><div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No Show Center shows saved yet.</div></Card>}
                {shows.map(show => <Card key={show.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <div><div style={{ fontWeight: 800 }}>{show.show_name}</div><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fmtDate(show.start_date)}{show.end_date ? ` - ${fmtDate(show.end_date)}` : ''}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{show.venue_name || ''} {show.city || ''} {show.state || ''}</div></div>
                    <span className="badge badge-green">{show.status}</span>
                  </div>
                  {show.secretary_name && <div style={{ marginTop: 8, fontSize: 12 }}>Secretary: {show.secretary_name} · {show.secretary_email}</div>}
                  {(show.horses || []).length > 0 && <div style={{ marginTop: 10, fontSize: 12 }}>{show.horses.map(h => <div key={h.id}>• {h.horse_name}: {h.rider_name || 'rider TBD'} {h.class_list ? `— ${h.class_list}` : ''}</div>)}</div>}
                </Card>)}
              </>
            )}

            {activeTab === 'Packet Builder' && <PacketBuilder />}

            {activeTab === 'Credentials' && (
              <>
                <div className="section-label">Add credential / card / document</div>
                <Card>
                  <Field label="Document type"><select className="form-input" value={credentialForm.credential_type} onChange={e => update(setCredentialForm, 'credential_type', e.target.value)}>{CREDENTIAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></Field>
                  <Field label="Applies to"><select className="form-input" value={credentialForm.applies_to} onChange={e => update(setCredentialForm, 'applies_to', e.target.value)}>{APPLIES_TO.map(t => <option key={t} value={t}>{t}</option>)}</select></Field>
                  <Field label="Horse"><select className="form-input" value={credentialForm.horse_id} onChange={e => update(setCredentialForm, 'horse_id', e.target.value)}><option value="">None / person document</option>{horses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}</select></Field>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Field label="Organization"><input className="form-input" value={credentialForm.organization} onChange={e => update(setCredentialForm, 'organization', e.target.value)} placeholder="USEF, AHA, USDF" /></Field>
                    <Field label="ID / card number"><input className="form-input" value={credentialForm.id_number} onChange={e => update(setCredentialForm, 'id_number', e.target.value)} /></Field>
                  </div>
                  <Field label="Display name"><input className="form-input" value={credentialForm.display_name} onChange={e => update(setCredentialForm, 'display_name', e.target.value)} placeholder="Cynthia USEF Card 2026" /></Field>
                  <Field label="Name on document"><input className="form-input" value={credentialForm.subject_name} onChange={e => update(setCredentialForm, 'subject_name', e.target.value)} /></Field>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Field label="Issue date"><input type="date" className="form-input" value={credentialForm.issue_date} onChange={e => update(setCredentialForm, 'issue_date', e.target.value)} /></Field>
                    <Field label="Expiration date"><input type="date" className="form-input" value={credentialForm.expiration_date} onChange={e => update(setCredentialForm, 'expiration_date', e.target.value)} /></Field>
                  </div>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 10 }}><input type="checkbox" checked={!!credentialForm.needs_front_back} onChange={e => update(setCredentialForm, 'needs_front_back', e.target.checked)} /> This card/document needs front and back images</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Field label="Front image/PDF"><input type="file" className="form-input" onChange={e => setSelectedFiles(p => ({ ...p, front: e.target.files?.[0] }))} /></Field>
                    <Field label="Back image/PDF"><input type="file" className="form-input" onChange={e => setSelectedFiles(p => ({ ...p, back: e.target.files?.[0] }))} /></Field>
                  </div>
                  <Field label="Single document / full PDF"><input type="file" className="form-input" onChange={e => setSelectedFiles(p => ({ ...p, document: e.target.files?.[0] }))} /></Field>
                  <Field label="Additional files"><input type="file" multiple className="form-input" onChange={e => setSelectedFiles(p => ({ ...p, files: e.target.files }))} /></Field>
                  <Field label="Notes"><textarea className="form-input" rows="2" value={credentialForm.notes} onChange={e => update(setCredentialForm, 'notes', e.target.value)} /></Field>
                  <button className="btn btn-primary" onClick={() => saveCredential()}>Save credential</button>
                </Card>

                <div className="section-label">Saved credentials</div>
                {credentials.length === 0 && <Card><div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No credentials saved yet.</div></Card>}
                {credentials.map(c => <Card key={c.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div><div style={{ fontWeight: 800 }}>{c.display_name || c.credential_type}</div><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.credential_type} · {c.organization || 'No org'} · {c.horse_name || c.subject_name || c.applies_to}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>ID: {c.id_number || '—'} · Expires: {fmtDate(c.expiration_date)}</div></div>
                    <span className="badge badge-green">{c.status}</span>
                  </div>
                  {(c.files || []).length > 0 && <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>{c.files.map(f => <a key={f.id} href={f.file_url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>{f.file_role}</a>)}</div>}
                </Card>)}
              </>
            )}

            {activeTab === 'USEF Vaccine Form' && (
              <>
                <Card style={{ borderLeft: '3px solid var(--cr-amber)', borderRadius: '0 var(--radius-lg) var(--radius-lg) 0' }}>
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>USEF vaccine form builder</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Stores the specific data needed for the USEF Equine Vaccination Record: Flu and EHV/Rhino vaccine names, dates, serial/lot numbers, expiration dates, and signature status. Upload the signed form as the document file.</div>
                </Card>
                <Card>
                  <Field label="Horse"><select className="form-input" value={vaccineForm.horse_id} onChange={e => update(setVaccineForm, 'horse_id', e.target.value)}><option value="">Select horse</option>{horses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}</select></Field>
                  <Field label="Horse name if not listed"><input className="form-input" value={vaccineForm.horse_name_text} onChange={e => update(setVaccineForm, 'horse_name_text', e.target.value)} /></Field>
                  <Field label="Owner name"><input className="form-input" value={vaccineForm.owner_name} onChange={e => update(setVaccineForm, 'owner_name', e.target.value)} /></Field>
                  <div className="section-label">Equine Influenza</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Field label="Date administered"><input type="date" className="form-input" value={vaccineForm.flu_date} onChange={e => update(setVaccineForm, 'flu_date', e.target.value)} /></Field>
                    <Field label="Vaccine name"><input className="form-input" value={vaccineForm.flu_name} onChange={e => update(setVaccineForm, 'flu_name', e.target.value)} /></Field>
                    <Field label="Serial / lot number"><input className="form-input" value={vaccineForm.flu_serial} onChange={e => update(setVaccineForm, 'flu_serial', e.target.value)} /></Field>
                    <Field label="Vaccine expiration"><input type="date" className="form-input" value={vaccineForm.flu_expiration} onChange={e => update(setVaccineForm, 'flu_expiration', e.target.value)} /></Field>
                  </div>
                  <div className="section-label">EHV / Rhinopneumonitis</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <Field label="Date administered"><input type="date" className="form-input" value={vaccineForm.ehv_date} onChange={e => update(setVaccineForm, 'ehv_date', e.target.value)} /></Field>
                    <Field label="Vaccine name"><input className="form-input" value={vaccineForm.ehv_name} onChange={e => update(setVaccineForm, 'ehv_name', e.target.value)} /></Field>
                    <Field label="Serial / lot number"><input className="form-input" value={vaccineForm.ehv_serial} onChange={e => update(setVaccineForm, 'ehv_serial', e.target.value)} /></Field>
                    <Field label="Vaccine expiration"><input type="date" className="form-input" value={vaccineForm.ehv_expiration} onChange={e => update(setVaccineForm, 'ehv_expiration', e.target.value)} /></Field>
                  </div>
                  <div className="section-label">Signature / proof</div>
                  <Field label="Veterinarian"><input className="form-input" value={vaccineForm.veterinarian_name} onChange={e => update(setVaccineForm, 'veterinarian_name', e.target.value)} /></Field>
                  <Field label="Clinic"><input className="form-input" value={vaccineForm.clinic_name} onChange={e => update(setVaccineForm, 'clinic_name', e.target.value)} /></Field>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 8 }}><input type="checkbox" checked={!!vaccineForm.vet_signature_present} onChange={e => update(setVaccineForm, 'vet_signature_present', e.target.checked)} /> Vet signature/stamp present on uploaded form</label>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 8 }}><input type="checkbox" checked={!!vaccineForm.owner_signature_present} onChange={e => update(setVaccineForm, 'owner_signature_present', e.target.checked)} /> Owner/agent signature present on uploaded form</label>
                  <Field label="Date signed"><input type="date" className="form-input" value={vaccineForm.date_signed} onChange={e => update(setVaccineForm, 'date_signed', e.target.value)} /></Field>
                  <Field label="Upload signed USEF vaccine form"><input type="file" className="form-input" onChange={e => setSelectedFiles(p => ({ ...p, document: e.target.files?.[0] }))} /></Field>
                  <Field label="Notes"><textarea className="form-input" rows="3" value={vaccineForm.notes} onChange={e => update(setVaccineForm, 'notes', e.target.value)} /></Field>
                  <button className="btn btn-primary" onClick={saveUsefVaccine}>Save USEF vaccine record</button>
                </Card>
              </>
            )}

            {activeTab === 'Secretaries' && (
              <>
                <div className="section-label">Add show secretary</div>
                <Card>
                  <Field label="Name"><input className="form-input" value={secretaryForm.name} onChange={e => update(setSecretaryForm, 'name', e.target.value)} /></Field>
                  <Field label="Email"><input className="form-input" value={secretaryForm.email} onChange={e => update(setSecretaryForm, 'email', e.target.value)} /></Field>
                  <Field label="Phone"><input className="form-input" value={secretaryForm.phone} onChange={e => update(setSecretaryForm, 'phone', e.target.value)} /></Field>
                  <Field label="Organization / show office"><input className="form-input" value={secretaryForm.organization} onChange={e => update(setSecretaryForm, 'organization', e.target.value)} /></Field>
                  <Field label="Notes"><textarea className="form-input" rows="3" value={secretaryForm.notes} onChange={e => update(setSecretaryForm, 'notes', e.target.value)} placeholder="Often handles Region 9, schooling dressage series, etc." /></Field>
                  <button className="btn btn-primary" onClick={saveSecretary}>Save secretary</button>
                </Card>
                <div className="section-label">Saved secretaries</div>
                {secretaries.map(s => <Card key={s.id}><div style={{ fontWeight: 800 }}>{s.name}</div><div style={{ fontSize: 13 }}>{s.email}</div><div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.organization || ''} {s.phone || ''}</div>{s.notes && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>{s.notes}</div>}</Card>)}
              </>
            )}

            {activeTab === 'Send Packet' && (
              <>
                <Card style={{ borderLeft: '3px solid var(--cr-pink)', borderRadius: '0 var(--radius-lg) var(--radius-lg) 0' }}>
                  <div style={{ fontWeight: 800 }}>Send packet to secretary</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Choose the show, secretary, and exact credential files to attach. This preserves front/back images and stores a sent history.</div>
                </Card>
                <Card>
                  <Field label="Show"><select className="form-input" value={packetForm.show_id} onChange={e => onSelectShowForPacket(e.target.value)}><option value="">Select show</option>{shows.map(s => <option key={s.id} value={s.id}>{s.show_name}</option>)}</select></Field>
                  <Field label="Secretary"><select className="form-input" value={packetForm.secretary_id} onChange={e => onSelectSecretaryForPacket(e.target.value)}><option value="">Select secretary</option>{secretaries.map(s => <option key={s.id} value={s.id}>{s.name} · {s.email}</option>)}</select></Field>
                  <Field label="To email"><input className="form-input" value={packetForm.to_email} onChange={e => update(setPacketForm, 'to_email', e.target.value)} /></Field>
                  <Field label="Subject"><input className="form-input" value={packetForm.subject} onChange={e => update(setPacketForm, 'subject', e.target.value)} /></Field>
                  <Field label="Email body"><textarea className="form-input" rows="8" value={packetForm.body} onChange={e => update(setPacketForm, 'body', e.target.value)} /></Field>
                  <div className="section-label">Attachments</div>
                  {credentials.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>No credentials uploaded yet.</div>}
                  {credentials.map(c => <div key={c.id} style={{ border: '1px solid var(--border-color)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{c.display_name || c.credential_type}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>{c.horse_name || c.subject_name || c.applies_to} · {c.credential_type}</div>
                    {(c.files || []).map(f => <label key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, padding: '3px 0' }}><input type="checkbox" checked={packetForm.credential_file_ids.includes(f.id)} onChange={() => toggleFile(f.id)} /> {f.file_role}: {f.file_name}</label>)}
                  </div>)}
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>{selectedCredentialFiles.length} file(s) selected.</div>
                  <button className="btn btn-primary" onClick={sendPacket}>Send packet</button>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>If SMTP is not configured, the app will save the packet/send attempt but will not email it yet.</div>
                </Card>
              </>
            )}

            {activeTab === 'Sent History' && (
              <>
                <div className="section-label">Sent packet history</div>
                {sends.length === 0 && <Card><div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No packet send history yet.</div></Card>}
                {sends.map(s => <Card key={s.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}><div><div style={{ fontWeight: 800 }}>{s.show_name || 'Show packet'}</div><div style={{ fontSize: 12 }}>{s.to_email}</div><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.subject}</div></div><span className="badge badge-green">{s.status}</span></div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{fmtDate(s.created_at)} {s.error_message ? `· ${s.error_message}` : ''}</div>
                </Card>)}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ShowsPage;
